import { queryClient } from '@/app/queryClient';
import { QUERY_KEYS } from '@/hooks/useDashboardQueries';
import { APP_CONFIG } from '@/lib/constants';
import { WsBaseEvent, WsConnectionState } from '@/types/websocket';
import { toast } from 'sonner';
import { formatINR } from '@/lib/utils';

export type WsEventListener = (event: WsBaseEvent) => void;
export type WsStatusListener = (state: WsConnectionState) => void;

class SocketManager {
  private static instance: SocketManager;
  private ws: WebSocket | null = null;
  private url: string;
  private reconnectAttempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private isExplicitlyClosed = true;
  private stateListeners: Set<WsStatusListener> = new Set();
  private eventListeners: Set<WsEventListener> = new Set();
  private recentEventSignatures: Set<string> = new Set();
  private mockInterval: ReturnType<typeof setInterval> | null = null;
  private isMockActive = false;

  private state: WsConnectionState = {
    status: 'OFFLINE',
    reconnectAttempt: 0,
    lastEventTime: null,
    lastError: null,
  };

  private constructor() {
    this.url = APP_CONFIG.wsUrl;

    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => {
        if (!this.isExplicitlyClosed && !this.isMockActive) {
          this.reconnect();
        }
      });

      window.addEventListener('offline', () => {
        this.clearReconnectTimer();
        this.updateState({
          status: 'OFFLINE',
          lastError: 'Device network offline',
        });
      });
    }
  }

  public static getInstance(): SocketManager {
    if (!SocketManager.instance) {
      SocketManager.instance = new SocketManager();
    }
    return SocketManager.instance;
  }

  public getState(): WsConnectionState {
    return { ...this.state };
  }

  public setUrl(url: string) {
    if (this.url !== url) {
      this.url = url;
      if (this.ws && !this.isExplicitlyClosed) {
        this.reconnect();
      }
    }
  }

  public getReconnectDelay(attempt: number): number {
    const delays = APP_CONFIG.wsReconnect.delays;
    const index = Math.min(attempt, delays.length - 1);
    return delays[index] ?? APP_CONFIG.wsReconnect.maxDelay;
  }

  public connect() {
    // Prevent duplicate active connections
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
      return;
    }

    this.isExplicitlyClosed = false;
    this.clearReconnectTimer();

    // If running in mock/demo fallback mode without live backend
    if (this.isMockActive) {
      this.startMockEventStream();
      return;
    }

    try {
      this.updateState({
        status: this.reconnectAttempt > 0 ? 'RECONNECTING' : 'OFFLINE',
        reconnectAttempt: this.reconnectAttempt,
      });

      this.ws = new WebSocket(this.url);

      this.ws.onopen = this.handleOpen.bind(this);
      this.ws.onmessage = this.handleMessage.bind(this);
      this.ws.onerror = this.handleError.bind(this);
      this.ws.onclose = this.handleClose.bind(this);
    } catch (err: unknown) {
      this.handleError(err);
      this.scheduleReconnect();
    }
  }

  public disconnect() {
    this.isExplicitlyClosed = true;
    this.clearReconnectTimer();
    this.stopMockEventStream();

    if (this.ws) {
      // Remove listeners to prevent reconnection loops during clean disconnect
      this.ws.onopen = null;
      this.ws.onmessage = null;
      this.ws.onerror = null;
      this.ws.onclose = null;
      this.ws.close();
      this.ws = null;
    }

    this.reconnectAttempt = 0;
    this.updateState({
      status: 'OFFLINE',
      reconnectAttempt: 0,
      lastError: null,
    });
  }

  public reconnect() {
    this.disconnect();
    this.connect();
  }

  public subscribeState(listener: WsStatusListener): () => void {
    this.stateListeners.add(listener);
    listener(this.getState());
    return () => {
      this.stateListeners.delete(listener);
    };
  }

  public subscribeEvents(listener: WsEventListener): () => void {
    this.eventListeners.add(listener);
    return () => {
      this.eventListeners.delete(listener);
    };
  }

  public setMockMode(active: boolean) {
    this.isMockActive = active;
    if (active) {
      if (this.ws) {
        this.ws.close();
        this.ws = null;
      }
      this.startMockEventStream();
    } else {
      this.stopMockEventStream();
      this.connect();
    }
  }

  private handleOpen() {
    const wasReconnecting = this.reconnectAttempt > 0;
    this.reconnectAttempt = 0;
    this.clearReconnectTimer();
    this.updateState({
      status: 'LIVE',
      reconnectAttempt: 0,
      lastError: null,
      lastEventTime: new Date().toISOString(),
    });

    // On reconnect, synchronize all active queries to recover any events missed during disconnection
    if (wasReconnecting) {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.DASHBOARD });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.SIGNALS });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.ORDERS });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.POSITIONS });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.RISK });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.STATUS });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.HEALTH });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.SETTINGS });
    }
  }

  private handleMessage(event: MessageEvent) {
    try {
      if (!event.data) return;

      const rawData = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
      this.processIncomingEvent(rawData);
    } catch {
      // Malformed or plain-text heartbeat (e.g. "ping" / "pong")
      if (typeof event.data === 'string' && (event.data === 'pong' || event.data === 'ping')) {
        this.updateState({ lastEventTime: new Date().toISOString() });
      }
    }
  }

  private handleError(error: unknown) {
    const errorMsg = error instanceof Error ? error.message : 'WebSocket connection error';
    this.updateState({
      lastError: errorMsg,
    });
  }

  private handleClose(event?: CloseEvent) {
    this.ws = null;
    if (this.isExplicitlyClosed) return;

    if (event && event.code === 1008) {
      // Handshake rejected by backend due to unauthenticated / expired session cookie
      this.isExplicitlyClosed = true;
      this.clearReconnectTimer();
      this.updateState({
        status: 'OFFLINE',
        reconnectAttempt: 0,
        lastError: 'Session unauthorized (1008)',
      });
      return;
    }

    const isNormalClosure = event && event.code === 1000;
    if (!isNormalClosure) {
      this.scheduleReconnect();
    } else {
      this.updateState({ status: 'OFFLINE', reconnectAttempt: 0 });
    }
  }

  private scheduleReconnect() {
    this.clearReconnectTimer();
    if (this.isExplicitlyClosed) return;

    this.reconnectAttempt += 1;
    const delay = this.getReconnectDelay(this.reconnectAttempt);

    this.updateState({
      status: 'RECONNECTING',
      reconnectAttempt: this.reconnectAttempt,
    });

    this.reconnectTimer = setTimeout(() => {
      this.connect();
    }, delay);
  }

  private clearReconnectTimer() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  /**
   * Process and validate incoming event safely.
   */
  public processIncomingEvent(event: WsBaseEvent) {
    const eventType = (event.type || event.event || '').toString().toLowerCase();
    const timestamp = event.timestamp || new Date().toISOString();
    const eventId = event.id || `${eventType}-${JSON.stringify(event.data || event.payload || '')}-${timestamp}`;

    // Deduplicate rapid repeat events within 5s window
    if (this.recentEventSignatures.has(eventId)) {
      return;
    }
    this.recentEventSignatures.add(eventId);
    setTimeout(() => {
      this.recentEventSignatures.delete(eventId);
    }, 5000);

    this.updateState({
      lastEventTime: typeof timestamp === 'number' ? new Date(timestamp).toISOString() : timestamp,
    });

    // Notify custom event listeners
    this.eventListeners.forEach((listener) => {
      try {
        listener(event);
      } catch {
        // Safe isolation
      }
    });

    // Invalidate corresponding TanStack query caches (REST remains authoritative source)
    this.invalidateQueriesForEvent(eventType);

    // Trigger subtle UI toasts only for high-priority operational events
    this.triggerToastNotification(eventType, event);
  }

  /**
   * TanStack Query Invalidation Mapping for exact backend event types
   */
  private invalidateQueriesForEvent(eventType: string) {
    switch (eventType) {
      case 'signal_received':
      case 'signal_rejected':
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.SIGNALS });
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.DASHBOARD });
        break;

      case 'signal_blocked':
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.SIGNALS });
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.DASHBOARD });
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.RISK });
        break;

      case 'signal_updated':
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.SIGNALS });
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.DASHBOARD });
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.ORDERS });
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.POSITIONS });
        break;

      case 'position_opened':
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.SIGNALS });
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.ORDERS });
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.POSITIONS });
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.DASHBOARD });
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.RISK });
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.PNL });
        break;

      case 'position_closed':
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.POSITIONS });
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.ORDERS });
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.DASHBOARD });
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.TRADES });
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.PNL });
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.TODAY });
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.WEEK });
        break;

      case 'cycle_completed':
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.DASHBOARD });
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.POSITIONS });
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.ORDERS });
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.SIGNALS });
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.STATUS });
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.HEALTH });
        break;

      case 'risk_halt':
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.RISK });
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.DASHBOARD });
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.SIGNALS });
        break;

      case 'square_off_triggered':
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.POSITIONS });
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.ORDERS });
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.DASHBOARD });
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.RISK });
        break;

      case 'settings_updated':
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.SETTINGS });
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.STATUS });
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.RISK });
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.DASHBOARD });
        break;

      case 'circuit_breaker_reset':
        // Handle backend broadcast event CIRCUIT_BREAKER_RESET (normalized to lowercase)
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.DASHBOARD });
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.RISK });
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.PNL });
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.STATUS });
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.HEALTH });
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.TODAY });
        break;

      default:
        // Ignore generic heartbeat / unrecognized payloads without error
        break;
    }
  }

  /**
   * Selective notification policy - only notify genuinely critical events
   */
  private triggerToastNotification(eventType: string, event: WsBaseEvent) {
    const data = (event.data || event.payload || {}) as Record<string, unknown>;

    switch (eventType) {
      case 'settings_updated': {
        toast.info('Runtime Settings Updated', {
          description: event.message || 'Bot configuration updated by operator.',
        });
        break;
      }

      case 'position_opened': {
        const symbol = (data.symbol || event.message || 'Position').toString();
        const qty = data.quantity ? ` (${data.quantity} shares)` : '';
        const orderInfo = data.order_id ? ` [Order: ${data.order_id}]` : '';
        toast.success(`Position Opened: ${symbol}${qty}`, {
          description: `Confirmed position open${orderInfo}.`,
        });
        break;
      }

      case 'position_closed': {
        const symbol = (data.symbol || event.message || 'Position').toString();
        const pnl = typeof data.realized_pnl === 'number' || typeof data.pnl === 'number'
          ? ` | Net P&L: ${formatINR((data.realized_pnl ?? data.pnl) as number, { showSign: true })}`
          : '';
        toast.info(`Position Closed: ${symbol}${pnl}`, {
          description: data.reason ? String(data.reason) : 'Target or stop-loss triggered.',
        });
        break;
      }

      case 'signal_blocked': {
        const symbol = (data.symbol || 'Signal').toString();
        const reasonCode = (data.outcome_reason_code || 'RISK_GATE').toString();
        const reason = data.outcome_reason ? String(data.outcome_reason) : `Blocked by ${reasonCode}`;
        toast.warning(`Risk Blocked: ${symbol}`, {
          description: reason,
        });
        break;
      }

      case 'signal_rejected': {
        const symbol = (data.symbol || 'Signal').toString();
        const reason = data.reason || data.outcome_reason || 'Validation filter declined signal.';
        toast.error(`Signal Rejected: ${symbol}`, {
          description: String(reason),
        });
        break;
      }

      case 'risk_halt': {
        const reason = (data.reason || event.message || 'Daily risk threshold triggered').toString();
        toast.error('Risk Halt Triggered', {
          description: reason,
        });
        break;
      }

      case 'square_off_triggered': {
        toast.info('Square-Off Triggered', {
          description: 'Intraday 3:15 PM EOD square-off cycle executed.',
        });
        break;
      }

      default:
        // No toast for routine cycle heartbeats or unparsed events
        break;
    }
  }

  private startMockEventStream() {
    this.stopMockEventStream();
    this.updateState({
      status: 'LIVE',
      reconnectAttempt: 0,
      lastError: null,
      lastEventTime: new Date().toISOString(),
    });

    // Simulate periodic live cycle events every 15s in demo mode
    this.mockInterval = setInterval(() => {
      this.processIncomingEvent({
        type: 'cycle_completed',
        timestamp: new Date().toISOString(),
        message: 'Background market data cycle completed',
        data: { cycle_time: new Date().toISOString() },
      });
    }, 15000);
  }

  private stopMockEventStream() {
    if (this.mockInterval) {
      clearInterval(this.mockInterval);
      this.mockInterval = null;
    }
  }

  private updateState(partial: Partial<WsConnectionState>) {
    this.state = { ...this.state, ...partial };
    this.stateListeners.forEach((listener) => {
      try {
        listener(this.getState());
      } catch {
        // Safe isolation
      }
    });
  }
}

export const socketManager = SocketManager.getInstance();
